import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  agenciesService, 
  AgencyEventRecord, 
  PaginationMeta,
  type ActivityCategory,
  type ActivityCategoryCounts,
  type AgencyActivity,
} from '@/services/agencies';
import { RelativeTime } from '@/components/RelativeTime';
import { TableSkeleton } from '@/components/skeletons';
import { Spinner } from '@/components/ui/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users, 
  FileText,
  CalendarCheck,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  Mail,
  Info,
  LogIn,
  LogOut,
  Globe,
  EyeOff,
  Eye,
  Trash2,
  Copy,
  UserCheck,
  UserX,
  Download,
  Link2,
  Unplug,
  AlertTriangle,
  ShieldCheck,
  Trophy,
  ScanEye,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { extraSubaccountBadges } from '@/lib/extra-subaccount-badges';
import { activityBadges, loginMethodLabel, platformEventBadges, providerLabel } from '@/lib/platform-event-badges';
import { webHref } from '@/lib/web-href';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

function TruncatedActivityBadge({ label, value, href }: { label: string; value: string; href?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation, value]);

  const valueNode = href ? (
    <a
      ref={ref as React.RefObject<HTMLAnchorElement>}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="truncate block max-w-[260px] text-primary underline underline-offset-2 hover:opacity-80"
    >
      {value}
    </a>
  ) : (
    <span ref={ref} className="truncate block max-w-[200px]">{value}</span>
  );

  const wrapper = (
    <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-default max-w-full">
      <span className="text-muted-foreground mr-1 shrink-0">{label}:</span>
      {valueNode}
    </Badge>
  );

  if (!isTruncated) return wrapper;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{wrapper}</TooltipTrigger>
        <TooltipContent side="top">
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ActivityLogTableProps {
  agencyId: string;
  /** The agency's signup, for "After signup" on its first submission, lead and appointment. */
  agencyCreatedAtUtc?: string;
}

export const EVENT_CONFIG: Record<string, { title: string; icon: React.ReactNode; color: string }> = {
  signup: { title: 'User Signed Up', icon: <Users className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  plan_started: { title: 'Plan Started', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  plan_changes: { title: 'Plan Changed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  downgraded: { title: 'Downgraded', icon: <Info className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  cancelled: { title: 'Cancelled', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  reactivated: { title: 'Reactivated', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  payment_failed: { title: 'Payment Failed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  payment_recovered: { title: 'Payment Recovered', icon: <CreditCard className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  trial_converted: { title: 'Trial Converted', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  downgrade_offer_claimed: { title: 'Offer Claimed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
  subaccount_created: { title: 'Subaccount Created', icon: <Building2 className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  team_member_invited: { title: 'Team Member Invited', icon: <Users className="h-4 w-4" />, color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  form_created: { title: 'Form Created', icon: <FileText className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  form_imported: { title: 'Form Imported', icon: <FileText className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  form_import_failed: { title: 'Form Import Failed', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  calendar_added: { title: 'Calendar Added', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' },
  logic_or_score_set: { title: 'Logic/Score Set', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  split_test_started: { title: 'Split Test Started', icon: <Info className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },

  form_embedded: { title: 'Form Embedded', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  first_submission_received: { title: 'First Submission', icon: <Mail className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  first_lead_captured: { title: 'First Lead', icon: <Users className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  first_appointment_booked: { title: 'First Appointment', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' },
  integration_connected: { title: 'Integration Connected', icon: <Info className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  integration_disconnected: { title: 'Integration Disconnected', icon: <Info className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  onboarding_completed: { title: 'Onboarding Completed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  checkout_failed: { title: 'Checkout Failed', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  onboarding_call_offer_shown: { title: 'Setup Offer Shown', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  onboarding_call_booked: { title: 'Setup Call Booked', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  onboarding_call_skipped: { title: 'Setup Call Skipped', icon: <XCircle className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  magic_link_sent: { title: 'Login Link Sent', icon: <Mail className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  magic_link_consumed: { title: 'Login Link Used', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  onboarding_step_changed: { title: 'Onboarding Step Changed', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  forms_paused_past_due: { title: 'Forms Paused (Past Due)', icon: <XCircle className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  xray_funds_granted: { title: 'X-Ray Funds Granted', icon: <CreditCard className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccount_offer_shown: { title: 'Extra Subaccount Offer Shown', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  extra_subaccount_agency_chosen: { title: 'Opened Plans from the Extra Subaccount Offer', icon: <Info className="h-4 w-4" />, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
  extra_subaccount_action_required: { title: 'Extra Subaccount Bank Check', icon: <CreditCard className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  extra_subaccount_purchased: { title: 'Extra Subaccount Purchased', icon: <Building2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccount_payment_failed: { title: 'Extra Subaccount Payment Failed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  extra_subaccount_removed: { title: 'Extra Subaccount Removed', icon: <Building2 className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  extra_subaccount_unlocked: { title: 'Subaccount Unlocked', icon: <Building2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccounts_changed: { title: 'Extra Subaccounts Changed', icon: <Building2 className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  // Step changes are left out by the API; the agency's Setup wizard card shows them.
  setup_wizard_started: { title: 'Setup wizard started', icon: <Info className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  setup_wizard_left: { title: 'Left the setup wizard', icon: <XCircle className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  setup_wizard_test_lead: { title: 'Setup wizard test lead sent', icon: <Mail className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  setup_wizard_published: { title: 'Setup wizard: form published', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  // The quiet account emails (back-end-next/src/lib/quiet-emails/).
  quiet_email_sent: { title: 'Quiet email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  quiet_emails_off: { title: 'Turned off quiet emails', icon: <XCircle className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  // The trial emails (back-end-next/src/lib/trial-emails/).
  trial_first_lead_email_sent: { title: 'First lead email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  trial_no_visits_email_sent: { title: 'No visitors yet email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  trial_no_leads_email_sent: { title: 'Visitors, no leads email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  trial_connect_email_sent: { title: 'Connect GoHighLevel email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  trial_left_wizard_email_sent: { title: 'Left the setup wizard email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  trial_emails_off: { title: 'Turned off the trial emails', icon: <XCircle className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  // Left without a card (back-end-next/src/lib/trial-emails/card-left.ts).
  checkout_viewed: { title: 'Reached the card step', icon: <CreditCard className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  card_left_email_sent: { title: 'Left without a card email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  card_left_reminder_sent: { title: 'Left without a card reminder sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  annual_fail_email_sent: { title: 'Annual payment failed email sent', icon: <Mail className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  // What a customer does day to day. active_day is never shown: the API leaves it out, the strip above reads it.
  logged_in: { title: 'Logged In', icon: <LogIn className="h-4 w-4" />, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  form_published: { title: 'Form Published', icon: <Globe className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  form_unpublished: { title: 'Form Unpublished', icon: <EyeOff className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  form_deleted: { title: 'Form Deleted', icon: <Trash2 className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  form_duplicated: { title: 'Form Duplicated', icon: <Copy className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  cancel_flow_left: { title: 'Left the Cancel Flow', icon: <LogOut className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  invite_accepted: { title: 'Invite Accepted', icon: <UserCheck className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  member_removed: { title: 'Team Member Removed', icon: <UserX className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  subaccount_deleted: { title: 'Subaccount Deleted', icon: <Trash2 className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  leads_exported: { title: 'Leads Exported', icon: <Download className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  leads_bulk_deleted: { title: 'Leads Bulk Deleted', icon: <Trash2 className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  webhook_created: { title: 'Webhook Added', icon: <Link2 className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  webhook_deleted: { title: 'Webhook Removed', icon: <Unplug className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  integration_broke: { title: 'Integration Needs Attention', icon: <AlertTriangle className="h-4 w-4" />, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  integration_fixed: { title: 'Integration Fixed', icon: <ShieldCheck className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  plans_viewed: { title: 'Viewed Plans', icon: <CreditCard className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  template_previewed: { title: 'Template Previewed', icon: <Eye className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  lead_milestone: { title: 'Lead Milestone', icon: <Trophy className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  xray_turned_on: { title: 'X-Ray Turned On', icon: <ScanEye className="h-4 w-4" />, color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  default: { title: 'Unknown Event', icon: <Info className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' }
};

type ActivityTab = 'all' | ActivityCategory;

/** The Activity Log's tabs (back-end-next/src/lib/platform-event-categories.ts places each event type). */
export const ACTIVITY_TABS: { value: ActivityTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'usage', label: 'Usage' },
  { value: 'building', label: 'Building' },
  { value: 'billing', label: 'Billing' },
  { value: 'emails', label: 'Emails' },
];

const DAY_MS = 24 * 60 * 60 * 1000;
/** The strip's squares: today and the 29 UTC days before it. */
const STRIP_DAYS = 30;

const utcDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The strip's 30 UTC dates ('YYYY-MM-DD'), oldest first, ending today. */
export function stripDays(now: Date = new Date()): string[] {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Array.from({ length: STRIP_DAYS }, (_, i) => utcDay(today - (STRIP_DAYS - 1 - i) * DAY_MS));
}

/**
 * "Active days": "N of 30", or "N of M days" with the date tracking started while active_day reaches back less than
 * 30 days. Null when the agency has no active_day yet.
 */
export function activeDaysSummary(
  activity: Pick<AgencyActivity, 'activeDays' | 'trackedSince'>,
  now: Date = new Date(),
): { text: string; trackedSince: string | null } | null {
  if (!activity.trackedSince) return null;
  const days = stripDays(now);
  const active = activity.activeDays.filter((day) => days.includes(day)).length;
  const today = Date.parse(`${days[days.length - 1]}T00:00:00Z`);
  const tracked = Math.max(1, Math.round((today - Date.parse(`${activity.trackedSince}T00:00:00Z`)) / DAY_MS) + 1);
  if (tracked >= STRIP_DAYS) return { text: `${active} of ${STRIP_DAYS}`, trackedSince: null };
  return { text: `${active} of ${tracked} ${tracked === 1 ? 'day' : 'days'}`, trackedSince: activity.trackedSince };
}

/** A 'YYYY-MM-DD' UTC date as "Oct 15". */
const formatUtcDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });

function StripStat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5 min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

/** Last active, active days, last login and live forms, then one square per UTC day for the last 30. */
function ActivityStrip({ activity }: { activity: AgencyActivity }) {
  const summary = activeDaysSummary(activity);
  const activeSet = new Set(activity.activeDays);
  const login = activity.lastLogin;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StripStat label="Last active">
          {activity.lastSeenAtUtc ? <RelativeTime date={activity.lastSeenAtUtc} /> : <span className="text-muted-foreground">No record</span>}
        </StripStat>
        <StripStat label="Active days">
          {summary ? (
            <>
              <span>{summary.text}</span>
              {summary.trackedSince && (
                <span className="block text-xs font-normal text-muted-foreground">tracked since {formatUtcDay(summary.trackedSince)}</span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">Not tracked yet</span>
          )}
        </StripStat>
        <StripStat label="Last login">
          {login ? (
            <>
              <RelativeTime date={login.atUtc} />
              {(login.method || login.userName) && (
                <span className="block text-xs font-normal text-muted-foreground truncate">
                  {[loginMethodLabel(login.method), login.userName].filter(Boolean).join(' · ')}
                </span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">No record</span>
          )}
        </StripStat>
        <StripStat label="Forms live">{activity.publishedForms.toLocaleString('en-US')}</StripStat>
      </div>
      <div className="max-w-[600px]">
        <div className="flex gap-1">
          {stripDays().map((day) => {
            const isActive = activeSet.has(day);
            const isTracked = !!activity.trackedSince && day >= activity.trackedSince;
            const state = isActive ? 'Active' : isTracked ? 'No activity' : 'Not tracked yet';
            return (
              <div
                key={day}
                title={`${formatUtcDay(day)}: ${state}`}
                className={`aspect-square flex-1 rounded-sm ${
                  isActive
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : isTracked
                      ? 'bg-muted'
                      : 'border border-dashed border-muted-foreground/30'
                }`}
              />
            );
          })}
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
          <span>30 days ago</span>
          <span>Today (UTC)</span>
        </div>
      </div>
    </div>
  );
}

export function ActivityLogTimeline({ agencyId, agencyCreatedAtUtc }: ActivityLogTableProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<AgencyEventRecord[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [tab, setTab] = useState<ActivityTab>('all');
  const [categoryCounts, setCategoryCounts] = useState<ActivityCategoryCounts | null>(null);
  const [activity, setActivity] = useState<AgencyActivity | null>(null);
  const [activityFailed, setActivityFailed] = useState(false);
  // Only the latest request lands, so a slow page from the tab you just left can't overwrite the new one.
  const requestRef = useRef(0);

  const loadEvents = useCallback(async (pageNum: number, append: boolean = false) => {
    const request = ++requestRef.current;
    if (append) {
      setIsFetchingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const response = await agenciesService.getAgencyEvents(agencyId, {
        page: pageNum,
        limit: 20,
        category: tab === 'all' ? undefined : tab,
      });
      if (request !== requestRef.current) return;
      setEvents(prev => append ? [...prev, ...response.data] : response.data);
      setMeta(response.meta);
      setCategoryCounts(response.meta.categoryCounts ?? null);
    } catch (error) {
      console.error('Failed to load activity log', error);
    } finally {
      if (request === requestRef.current) {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    }
  }, [agencyId, tab]);

  useEffect(() => {
    loadEvents(1, false);
  }, [loadEvents]);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    setActivityFailed(false);
    agenciesService.getAgencyActivity(agencyId)
      .then(data => { if (!cancelled) setActivity(data); })
      .catch(error => {
        console.error('Failed to load agency activity', error);
        if (!cancelled) setActivityFailed(true);
      });
    return () => { cancelled = true; };
  }, [agencyId]);

  // A new tab starts again from page 1 (loadEvents changes with the tab, so the effect above reloads it).
  const selectTab = (value: string) => {
    setTab(value as ActivityTab);
    setPage(1);
  };

  const hasMorePages = meta ? page < meta.totalPages : false;

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (isFetchingMore) return;
    if (observerRef.current) observerRef.current.disconnect();

    if (node && hasMorePages) {
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadEvents(nextPage, true);
        }
      }, { rootMargin: '200px' });
      observerRef.current.observe(node);
    }
  }, [isFetchingMore, hasMorePages, page, loadEvents]);

  const renderMetadata = (event: AgencyEventRecord) => {
    const md = event.metadata || {};
    if (Object.keys(md).length === 0 && !event.user) return <span className="text-muted-foreground">—</span>;

    const badges: { label: string; value: string; href?: string }[] = [];

    // Helper to add badges safely (href makes the value a clickable link)
    const addBadge = (label: string, value: any, href?: string) => {
      if (value) badges.push({ label, value: String(value), href });
    };

    // Surface the import URL first for imports (clickable, easy to investigate).
    if (event.eventType === 'form_import_failed' || event.eventType === 'form_imported') {
      const importUrl = md.inputUrl || md.sourceUrl;
      if (importUrl) addBadge('URL', importUrl, webHref(importUrl));
      if (md.platform) addBadge('Platform', md.platform);
      if (md.errorCode) addBadge('Code', md.errorCode);
      if (md.stage) addBadge('Stage', md.stage);
    }

    // Extra subaccounts: the count change, and why or how it happened.
    if (event.eventType.startsWith('extra_subaccount')) {
      for (const { label, value } of extraSubaccountBadges(md)) addBadge(label, value);
    }

    for (const { label, value } of platformEventBadges(event.eventType, md)) addBadge(label, value);

    if (md.formName) addBadge('Form', md.formName);
    if (md.subaccountName) addBadge('Subaccount', md.subaccountName);
    if (md.planName) addBadge('Plan', md.planName);
    if (md.provider) addBadge('Integration', providerLabel(md.provider));

    if (md.splitTestName) addBadge('Test', md.splitTestName);
    
    if (md.errorMessage) addBadge('Error', md.errorMessage);
    if (md.planKey || md.plan) addBadge('Plan', md.planKey || md.plan);

    for (const { label, value } of activityBadges(event, { agencyCreatedAtUtc, timezone: user?.timezone })) addBadge(label, value);

    if (event.eventType === 'form_embedded' && md.embed_url) {
      addBadge('Website', md.embed_url, webHref(md.embed_url));
    }

    // If backfilled flag is true
    if (md.backfilled) {
      badges.push({ label: 'Source', value: 'Backfilled data' });
    }

    if (event.user) {
      let actorLabel = 'Performed By';
      switch (event.eventType) {
        case 'team_member_invited': actorLabel = 'Invited By'; break;
        case 'form_created':
        case 'subaccount_created': actorLabel = 'Created By'; break;
        case 'calendar_added': actorLabel = 'Added By'; break;
        case 'integration_connected': actorLabel = 'Connected By'; break;
        case 'integration_disconnected': actorLabel = 'Disconnected By'; break;
        case 'plan_changes':
        case 'plan_started': actorLabel = 'Changed By'; break;
        case 'logic_or_score_set': actorLabel = 'Set By'; break;
        case 'split_test_started': actorLabel = 'Started By'; break;
        case 'checkout_failed': actorLabel = 'Attempted By'; break;
        case 'signup': actorLabel = 'User'; break; // Keep User for signup
        case 'cancelled': actorLabel = 'Cancelled By'; break;
        case 'logged_in':
        case 'invite_accepted':
        case 'plans_viewed':
        case 'template_previewed':
        case 'cancel_flow_left': actorLabel = 'User'; break;
        case 'member_removed':
        case 'webhook_deleted': actorLabel = 'Removed By'; break;
        case 'form_deleted':
        case 'subaccount_deleted':
        case 'leads_bulk_deleted': actorLabel = 'Deleted By'; break;
        case 'form_published': actorLabel = 'Published By'; break;
        case 'form_unpublished': actorLabel = 'Unpublished By'; break;
        case 'form_duplicated': actorLabel = 'Duplicated By'; break;
        case 'leads_exported': actorLabel = 'Exported By'; break;
        case 'webhook_created': actorLabel = 'Added By'; break;
        case 'integration_fixed': actorLabel = 'Fixed By'; break;
        case 'xray_turned_on': actorLabel = 'Turned On By'; break;
      }
      
      addBadge(actorLabel, event.user.firstName ? `${event.user.firstName} ${event.user.lastName || ''}`.trim() : event.user.email);
    }

    if (badges.length === 0) {
      // Just render the first key-value pair if we don't know it
      const entries = Object.entries(md).filter(([k]) => k !== 'backfilled' && k !== 'htmlSnapshot');
      if (entries.length > 0) {
        addBadge(entries[0][0], entries[0][1]);
      }
    }

    return (
      <div className="flex flex-wrap gap-1.5 mt-1">
        {badges.map((b, i) => (
          <TruncatedActivityBadge key={i} label={b.label} value={b.value} href={b.href} />
        ))}
      </div>
    );
  };

  const tabLabel = ACTIVITY_TABS.find(t => t.value === tab)?.label ?? 'All';

  return (
    <div className="rounded-lg border bg-card p-4 sm:p-6">
      {!activityFailed && (
        <div className="mb-5">
          {activity ? (
            <ActivityStrip activity={activity} />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-10" />)}
              </div>
              <Skeleton className="h-4 max-w-[600px]" />
            </div>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={selectTab} className="mb-4">
        <TabsList variant="line" className="w-full justify-start border-b rounded-none overflow-x-auto">
          {ACTIVITY_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="flex-none">
              {t.label}
              {categoryCounts && (
                <span className="text-xs text-muted-foreground tabular-nums">{categoryCounts[t.value].toLocaleString('en-US')}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-6">
          <TableSkeleton columns={1} rows={5} />
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-muted-foreground py-12">
          <Clock className="h-12 w-12 mb-4 opacity-20" />
          {tab === 'all' ? (
            <>
              <p className="text-lg font-medium text-foreground">No activity recorded yet</p>
              <p className="text-sm mt-1">Events will appear here as the agency interacts with the platform.</p>
            </>
          ) : (
            <p className="text-lg font-medium text-foreground">No {tabLabel} events yet</p>
          )}
        </div>
      ) : (
        <>
          <div className="relative border-l-2 border-muted pl-6 ml-4 space-y-4 py-2">
            {events.map((event, index) => {
              const config = EVENT_CONFIG[event.eventType] || EVENT_CONFIG.default;
              const isLast = index === events.length - 1;
              
              return (
                <div key={event.id} className="relative">
                  <div className={`absolute -left-[39px] top-0 p-1.5 rounded-full ring-4 ring-card flex items-center justify-center ${config.color} bg-background`}>
                    {config.icon}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-foreground text-base">{config.title}</span>
                      </div>
                      {renderMetadata(event)}
                    </div>
                    
                    <div className="shrink-0 text-sm text-muted-foreground">
                      <RelativeTime date={event.createdAtUtc} />
                    </div>
                  </div>
                  
                  {/* Optional subtle divider between events, unless it's the last one */}
                  {!isLast && (
                    <div className="mt-4 border-b border-border/50 hidden sm:block" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Sentinel element for infinite scroll — only when there are more pages */}
          {hasMorePages && (
            <div ref={sentinelRef} className="flex items-center justify-center py-4">
              {isFetchingMore && <Spinner size="sm" />}
            </div>
          )}
        </>
      )}

      {meta && (
        <div className="mt-6 pt-4 border-t flex items-center justify-between">
          <div className="text-sm text-muted-foreground hidden sm:block">
            Showing <span className="font-medium text-foreground">{events.length}</span> of{' '}
            <span className="font-medium text-foreground">{meta.total}</span> events
          </div>
        </div>
      )}
    </div>
  );
}
